import React, {useEffect} from 'react';
import { Turnstile } from '@marsidev/react-turnstile'
import { useNavigate } from 'react-router-dom';
import {
    Button, Input, MessageBar, Spinner, Field, Checkbox,
    Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, DialogTrigger,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { Form, useParams } from 'react-router-dom';
import {useGetBasicStorefrontQuery, useSubmitCustomerSignupMutation} from "../Api/Api";
import {ICustomerSignupRequest} from "../Api/Models/Customer";
import MarkdownView from 'react-showdown';
import {useLocalizationFor} from "../Components/Helpers/useLocalization";
import LoginPageShell, { useLoginPageStyles } from "../Components/Auth/LoginPageShell";
import { useHostTenancyRedirect } from "../Components/Helpers/useHostTenancyRedirect";
// Cloudflare Turnstile site key. The production key is the default so no deployed environment
// changes behavior; window.config.turnstileSiteKey overrides it where needed. The production key is
// domain-restricted at Cloudflare, so on localhost it cannot load at all and the widget renders
// "Unable to connect to website" — which is why local config points at the always-passes TEST key.
// Whatever is used here must pair with the secret the storefront API validates against.
const PRODUCTION_TURNSTILE_SITE_KEY = '0x4AAAAAABpv3_PultviAqys';
const turnstileSiteKey = window.config?.turnstileSiteKey || PRODUCTION_TURNSTILE_SITE_KEY;

const SignupPage: React.FC = () => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const emailAddress = urlParams.get('email');
    const { organization, storefront } = useParams<{ organization: string, storefront: string }>();
    // On a custom domain, a deep link to another org's signup bounces to this domain's own storefront.
    useHostTenancyRedirect(organization);
    const navigate = useNavigate();
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [submitResult, setSubmitResult] = React.useState<string>();

    const message = new URLSearchParams(window.location.search).get('message');
    const [signupApi] = useSubmitCustomerSignupMutation();
    const [canSubmit, setCanSubmit] = React.useState<boolean>(false);
    const [token, setToken] = React.useState<string>();
    const [agreed, setAgreed] = React.useState<boolean>(false);
    // Terms live in a dialog rather than inline on the form, the same way checkout presents them: a
    // wall of markdown between the fields and the submit button is what gets scrolled past.
    const [termsOpen, setTermsOpen] = React.useState<boolean>(false);
    // The shopper must scroll the whole thing before they can agree.
    const [termsScrolledToBottom, setTermsScrolledToBottom] = React.useState<boolean>(false);
    const termsScrollRef = React.useRef<HTMLDivElement>(null);

    const handleTermsScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 8) {
            setTermsScrolledToBottom(true);
        }
    };
    const [signupRecord, setSignupRecord] = React.useState<ICustomerSignupRequest>({
        token: "",
        companyName: "",
        email: emailAddress ?? "",
        firstName: "",
        lastName: "",
        phone: "",
        storefront: ""
    });
    
    const sSettings = useGetBasicStorefrontQuery({ organization: organization ?? '', storefront: storefront ?? '' }, {skip: !organization || !storefront});
    const L = useLocalizationFor(sSettings.data?.localizationConfig);
    // The same parts the sign-in page applies. The shell alone only reaches the backdrop, card,
    // logo and heading, so without this the sign-up form keeps Fluent's light defaults and a
    // branded card ends up holding white fields and a grey button.
    const s = useLoginPageStyles(sSettings.data?.loginPageConfig);
    // Slot styles pass through undefined when unset, so `label={{children, style: undefined}}` and
    // `input={undefined}` render exactly what the plain props did.
    const labelSlot = (text: string) => ({ children: text, style: s.cardText });
    const inputSlot = s.inputText ? { style: s.inputText } : undefined;

    useEffect(() => {
        if (termsOpen) {
            const el = termsScrollRef.current;
            if (el && el.scrollHeight - el.clientHeight < 8) {
                setTermsScrolledToBottom(true);
            }
        }
    }, [termsOpen]);

    useEffect(() => {
        if(sSettings.data && !sSettings.data.allowSignup) {
            navigate('/home')
        }
    }, [sSettings]);
    

    const onSubmit = async () => {
        setSubmitting(true)

        if(storefront && organization && token) {
            const result = await signupApi({
                organization: organization,
                storefront: storefront,
                request: {
                    ...signupRecord,
                    organization: organization,
                    storefront: storefront,
                    token: token
                }
            });
            setSubmitResult(result.data?.message);
        }
        setSubmitting(false);

    }
    
    const onHome = () => {
        navigate('/home')
    }

    const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        onSubmit().then();
    }
    
    const onValueChange = (newValue: ICustomerSignupRequest) => {
        setSignupRecord((_) => ({...newValue}))
    }
    
    useEffect(() => {
        const canSubmit = canSignup();
        setCanSubmit(canSubmit)
    }, [sSettings.data?.allowSignup, organization, storefront, signupRecord.email, signupRecord.firstName, signupRecord.lastName, token, agreed]);
    
    const canSignup = () => {
        if(!sSettings.data){
            return false;
        }
        if(!sSettings.data.allowSignup){
            return false;
        }
        if(!organization || !storefront){
            return false;
        }
        if(!signupRecord.email || !signupRecord.firstName || !signupRecord.lastName) {
            return false;
        }
        if(sSettings.data.signupTermsAndConditions && !agreed) {
            return false;
        }
        return !token ? false : true;
    }
    
    return(
        <LoginPageShell
            config={sSettings.data?.loginPageConfig}
            storefrontName={sSettings.data?.name}
            backgroundColor={sSettings.data?.backgroundColor}
            textColor={sSettings.data?.textColor}
            favicon={sSettings.data?.favicon}
            containerClassName='signup-container'
            boxClassName='signup-box'
        >
            {sSettings.isLoading && <div className={'signup-loading'}><Spinner label={L('localization_common_loading')} /></div>}
            {!sSettings.isLoading && submitting && <Spinner label={L('localization_auth_submitting')} />}
            {!sSettings.isLoading && !submitting && submitResult && <div className={'signup-results'}>{submitResult}</div>}
            {!sSettings.isLoading && !submitting && !submitResult && sSettings.data?.allowSignup && organization && storefront && (
                <React.Fragment>
                    <div className={'signup-header'}>
                        {/* The storefront name (and any configured logo) is rendered by the shell. */}
                        <h3>{L('localization_auth_signup_heading')}</h3>
                        {message && <MessageBar style={{marginBottom: '1em', ...s.message}}>{message}</MessageBar>}
                    </div>
                    <Form onSubmit={onFormSubmit}>
                        <div className='signup-form'>
                            <Field required label={labelSlot(L('localization_auth_email_address'))}>
                                <Input required placeholder={L('localization_auth_email_address')} onChange={(_, v) => onValueChange({...signupRecord, email: v.value})} value={signupRecord?.email} style={s.input} input={inputSlot}/>
                            </Field>
                            <Field required label={labelSlot(L('localization_auth_first_name'))}>
                                <Input required placeholder={L('localization_auth_first_name')} onChange={(_, v) => onValueChange({...signupRecord, firstName: v.value})} value={signupRecord?.firstName} style={s.input} input={inputSlot}/>
                            </Field>
                            <Field required label={labelSlot(L('localization_auth_last_name'))}>
                                <Input required placeholder={L('localization_auth_last_name')} onChange={(_, v) => onValueChange({...signupRecord, lastName: v.value})} value={signupRecord?.lastName} style={s.input} input={inputSlot}/>
                            </Field>
                            <Field label={labelSlot(L('localization_auth_company_name'))}>
                                <Input placeholder={L('localization_auth_company_name')} onChange={(_, v) => onValueChange({...signupRecord, companyName: v.value})} value={signupRecord?.companyName} style={s.input} input={inputSlot}/>
                            </Field>
                            <Field label={labelSlot(L('localization_auth_phone_number'))}>
                                <Input placeholder={L('localization_auth_phone_number')} onChange={(_, v) => onValueChange({...signupRecord, phone: v.value})} value={signupRecord?.phone} style={s.input} input={inputSlot}/>
                            </Field>
                        </div>
                        {sSettings.data?.signupTermsAndConditions &&
                            <div className={'signup-terms'} style={{margin: '1em 0'}}>
                                <Button appearance='subtle' className='terms-view-btn' style={s.secondaryButton} onClick={() => setTermsOpen(true)}>
                                    {L('localization_auth_open_terms')}
                                </Button>
                                {/* The toggle stays on the form, where the shopper is working, but it
                                    cannot be ticked until the terms have been opened and read through
                                    -- otherwise the dialog is just a link nobody follows. */}
                                <div style={{marginTop: '0.5em'}}>
                                    <Checkbox
                                        required
                                        checked={agreed}
                                        disabled={!termsScrolledToBottom}
                                        label={labelSlot(L('localization_auth_agree_terms'))}
                                        onChange={(_, d) => setAgreed(d.checked === true)}
                                    />
                                </div>
                                {!termsScrolledToBottom &&
                                    <div style={{fontSize: '0.9em', opacity: 0.8, ...s.cardText}}>
                                        {L('localization_auth_terms_scroll_prompt')}
                                    </div>
                                }
                                <Dialog open={termsOpen} onOpenChange={(_, d) => setTermsOpen(d.open)}>
                                    <DialogSurface style={{ maxWidth: '600px', width: '90vw' }}>
                                        <DialogBody>
                                            <DialogTitle
                                                action={
                                                    <DialogTrigger action='close'>
                                                        <Button appearance='subtle' icon={<DismissRegular />} aria-label='close' />
                                                    </DialogTrigger>
                                                }
                                            >
                                                {L('localization_auth_terms_title')}
                                            </DialogTitle>
                                            <DialogContent>
                                                <div
                                                    ref={termsScrollRef}
                                                    onScroll={handleTermsScroll}
                                                    style={{ maxHeight: '50vh', overflowY: 'auto' }}
                                                >
                                                    <MarkdownView
                                                        markdown={sSettings.data?.signupTermsAndConditions ?? ''}
                                                        options={{ tables: true, emoji: true }}
                                                    />
                                                </div>
                                            </DialogContent>
                                            <DialogActions>
                                                <DialogTrigger action='close'>
                                                    <Button appearance='primary'>
                                                        {L('localization_common_close', 'Close')}
                                                    </Button>
                                                </DialogTrigger>
                                            </DialogActions>
                                        </DialogBody>
                                    </DialogSurface>
                                </Dialog>
                            </div>
                        }
                        <div style={{paddingTop: '1em'}}>
                            <Turnstile siteKey={turnstileSiteKey} onSuccess={(token) => setToken(token)}/>
                        </div>
                        <div className={'signup-button'}>
                            {/* The configured look is dropped while disabled: an inline background
                                beats Fluent's disabled styling, so a styled button would look live
                                while the form is still incomplete. */}
                            <Button
                                appearance='primary' disabled={!canSubmit} type="submit" style={{width: '100%', marginTop: '1em', ...(canSubmit ? s.primaryButton : undefined)}}>
                                {L('localization_auth_submit_button')}
                            </Button>
                        </div>

                    </Form>
                </React.Fragment>
            )}
            {!sSettings.isLoading && !submitting && !submitResult && (!organization || !storefront || !sSettings.data?.allowSignup) && (
                <div className={'signup-alert'}>
                    <MessageBar style={{marginBottom: '1em', ...s.message}}>
                        {sSettings.error ? L('localization_auth_signup_error_retry') : L('localization_auth_signup_missing_info')}
                    </MessageBar>
                    <Button appearance='primary' onClick={onHome} style={{width: '100%', marginTop: '1em', ...s.primaryButton}}>{L('localization_common_go_home')}</Button>
                </div>
            )}
        </LoginPageShell>)
}

export default SignupPage;